import { useState, useEffect, useRef } from 'react';
import type { Request, TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeByOppId, sumUniqueByOpp, isReqAged, ageSeverity, formatReqAge, splitUserSCNames, getRequestURL } from '../../utils/opportunities';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { RangeSlider } from '../ui/RangeSlider';
import { OfferingChart, bucketSolution } from '../charts/OfferingChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useRequests } from '../../context/RequestsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-824" data-mp="1gwj3vzxifv4t">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    requests
  } = useRequests();
  const all = getFiltered('sd', state, requests);
  const ip = all.filter(r => r.req_status === 'In Progress');
  const cls = all.filter(r => r.req_status === 'Closed');
  const aged = ip.filter(r => isReqAged(r));
  const scs = new Set(ip.flatMap(r => splitUserSCNames(r.user_sc_name))).size;
  const oppGnarr = sumUniqueByOpp(all, r => r.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeByOppId(requests.filter(r => {
    if (r.opp_adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && r.req_sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!r.opp_close_q) return false;
      const [yr, qt] = r.opp_close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, r) => s + (r.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;

  // Merge requests by opp_id: combine SC names, keep all requests for age links
  interface MergedRequest extends Request {
    mergedSc: string;
    displayStatus: string;
    allRequests: Request[];
  }
  const requestMap = new Map<string, MergedRequest>();
  for (const r of all) {
    const existing = requestMap.get(r.opp_id);
    if (existing) {
      const scNames = new Set([...existing.mergedSc.split(',').map(s => s.trim()).filter(Boolean), ...r.user_sc_name.split(',').map(s => s.trim()).filter(Boolean)]);
      existing.mergedSc = [...scNames].join(', ');
      existing.allRequests.push(r);
      if (!existing.displayStatus && r.opp_status) existing.displayStatus = r.opp_status;
    } else {
      requestMap.set(r.opp_id, {
        ...r,
        mergedSc: r.user_sc_name,
        displayStatus: r.opp_status,
        allRequests: [r]
      });
    }
  }
  const mergedRequests = [...requestMap.values()];
  const [searchId, setSearchId] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [searchSc, setSearchSc] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const subDropdownRef = useRef<HTMLDivElement>(null);

  // Derived ranges from data
  const ages = mergedRequests.map(r => r.req_age ?? 0);
  const gnarrs = mergedRequests.map(r => r.opp_gnarr_grand_total ?? 0);
  const dataAgeMin = Math.min(...ages);
  const dataAgeMax = Math.max(...ages);
  const dataGnarrMin = Math.min(...gnarrs);
  const dataGnarrMax = Math.max(...gnarrs);
  const [ageMin, setAgeMin] = useState(dataAgeMin);
  const [ageMax, setAgeMax] = useState(dataAgeMax);
  const [gnarrMin, setGnarrMin] = useState(dataGnarrMin);
  const [gnarrMax, setGnarrMax] = useState(dataGnarrMax);

  // deals loads asynchronously (empty on first render), so the useState initializers above
  // capture Math.min/max(...[]) === ±Infinity and never resync — once real data arrives,
  // pull the range in once so the age/gnarr filters aren't stuck rejecting every row.
  // Also clamp the slider values whenever the data range changes to prevent overflow.
  useEffect(() => {
    if (ageMin === Infinity && Number.isFinite(dataAgeMin)) {
      setAgeMin(dataAgeMin);
      setAgeMax(dataAgeMax);
    } else if (Number.isFinite(dataAgeMin) && Number.isFinite(dataAgeMax)) {
      // Clamp existing values to new data range
      setAgeMin(prev => Math.max(dataAgeMin, Math.min(prev, dataAgeMax)));
      setAgeMax(prev => Math.min(dataAgeMax, Math.max(prev, dataAgeMin)));
    }
  }, [dataAgeMin, dataAgeMax]);
  useEffect(() => {
    if (gnarrMin === Infinity && Number.isFinite(dataGnarrMin)) {
      setGnarrMin(dataGnarrMin);
      setGnarrMax(dataGnarrMax);
    } else if (Number.isFinite(dataGnarrMin) && Number.isFinite(dataGnarrMax)) {
      // Clamp existing values to new data range
      setGnarrMin(prev => Math.max(dataGnarrMin, Math.min(prev, dataGnarrMax)));
      setGnarrMax(prev => Math.min(dataGnarrMax, Math.max(prev, dataGnarrMin)));
    }
  }, [dataGnarrMin, dataGnarrMax]);
  const [selectedCloseQs, setSelectedCloseQs] = useState<string[]>([]);
  const [closeQDropdownOpen, setCloseQDropdownOpen] = useState(false);
  const closeQDropdownRef = useRef<HTMLDivElement>(null);
  const [offeringTop, setOfferingTop] = useState(8);
  const [offeringTotal, setOfferingTotal] = useState(1);
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [othersLabels, setOthersLabels] = useState<string[]>([]);
  const handleSolutionClick = (name: string) => {
    setSelectedSolution(prev => prev === name ? null : name);
  };
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);
  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target as Node)) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [regionDropdownOpen]);
  useEffect(() => {
    if (!subDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (subDropdownRef.current && !subDropdownRef.current.contains(e.target as Node)) {
        setSubDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [subDropdownOpen]);
  useEffect(() => {
    if (!closeQDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (closeQDropdownRef.current && !closeQDropdownRef.current.contains(e.target as Node)) {
        setCloseQDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [closeQDropdownOpen]);
  const allStatuses = [...new Set(mergedRequests.map(r => r.displayStatus).filter(Boolean))].sort();
  const allRegions = [...new Set(mergedRequests.map(r => r.opp_region).filter(Boolean))].sort();

  // Build region ↔ sub mappings from data
  const regionToSubs = new Map<string, Set<string>>();
  const subToRegion = new Map<string, string>();
  for (const r of mergedRequests) {
    if (!r.opp_region || !r.req_sub_region) continue;
    if (!regionToSubs.has(r.opp_region)) regionToSubs.set(r.opp_region, new Set());
    regionToSubs.get(r.opp_region)!.add(r.req_sub_region);
    if (!subToRegion.has(r.req_sub_region)) subToRegion.set(r.req_sub_region, r.opp_region);
  }

  // Subs available: if regions are selected, only show subs under those regions; otherwise all
  const availableSubs = selectedRegions.length > 0 ? [...new Set(selectedRegions.flatMap(r => [...(regionToSubs.get(r) ?? [])]))].sort() : [...new Set(mergedRequests.map(r => r.req_sub_region).filter(Boolean))].sort();

  // Auto-select region when a sub is selected and no region is active;
  // auto-clear region when deselecting a sub leaves no subs under that region.
  const autoSelectedRegionRef = useRef<string | null>(null);
  const handleSubToggle = (sub: string) => {
    setSelectedSubs(prev => {
      const next = prev.includes(sub) ? prev.filter(x => x !== sub) : [...prev, sub];
      // If adding a sub and no regions selected, auto-select the parent region
      if (!prev.includes(sub) && selectedRegions.length === 0) {
        const parent = subToRegion.get(sub);
        if (parent) {
          setSelectedRegions([parent]);
          autoSelectedRegionRef.current = parent;
        }
      }
      // If removing a sub and an auto-selected region has no remaining subs, clear it
      if (prev.includes(sub) && autoSelectedRegionRef.current) {
        const regionSubs = regionToSubs.get(autoSelectedRegionRef.current);
        const hasRemaining = next.some(s => regionSubs?.has(s));
        if (!hasRemaining) {
          setSelectedRegions([]);
          autoSelectedRegionRef.current = null;
        }
      }
      return next;
    });
  };

  // Clean up selectedSubs when region filter removes them
  const effectiveSubs = selectedSubs.filter(s => availableSubs.includes(s));
  const allCloseQs = [...new Set(mergedRequests.map(r => r.opp_close_q).filter(Boolean))].sort();
  const mergedFiltered = mergedRequests.filter(r => {
    if (searchId && !r.opp_id.toLowerCase().includes(searchId.toLowerCase())) return false;
    if (searchAccount && !r.acc_name.toLowerCase().includes(searchAccount.toLowerCase())) return false;
    if (searchSc && !r.mergedSc.toLowerCase().includes(searchSc.toLowerCase())) return false;
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.displayStatus)) return false;
    if (selectedRegions.length > 0 && !selectedRegions.includes(r.opp_region)) return false;
    if (effectiveSubs.length > 0 && !effectiveSubs.includes(r.req_sub_region)) return false;
    if (r.req_age !== null && (r.req_age < ageMin || r.req_age > ageMax)) return false;
    if (r.opp_gnarr_grand_total !== null && (r.opp_gnarr_grand_total < gnarrMin || r.opp_gnarr_grand_total > gnarrMax)) return false;
    if (selectedCloseQs.length > 0 && !selectedCloseQs.includes(r.opp_close_q)) return false;
    if (selectedSolution) {
      const requestSolution = bucketSolution(r.req_products || '');
      if (selectedSolution === 'Others') {
        if (!othersLabels.includes(requestSolution)) return false;
      } else if (requestSolution !== selectedSolution) {
        return false;
      }
    }
    return true;
  });
  const STAGE_COLORS: Record<string, {
    bg: string;
    fg: string;
  }> = {
    'Prospect': {
      bg: '#F3F4F6',
      fg: '#6B7280'
    },
    'Qualification': {
      bg: '#EFF6FF',
      fg: '#3B82F6'
    },
    'Influence': {
      bg: '#F0F9FF',
      fg: '#0EA5E9'
    },
    'Solutioning': {
      bg: '#F5F3FF',
      fg: '#8B5CF6'
    },
    'Customer Commit': {
      bg: '#FFF7ED',
      fg: '#F97316'
    },
    'Order': {
      bg: '#FFFBEB',
      fg: '#D97706'
    },
    'Closed - Booked': {
      bg: '#F0FDF4',
      fg: '#16A34A'
    }
  };
  const statusBadge = (status: string) => {
    const c = STAGE_COLORS[status] || {
      bg: '#F9FAFB',
      fg: '#374151'
    };
    return <span className="badge" style={{
      background: c.bg,
      color: c.fg,
      fontSize: 8
    }} data-id="element-825" data-mp="25554rzfwxim6">{status}</span>;
  };
  const mergedColumns: SortableColumn<MergedRequest>[] = [{
    key: 'opp_id',
    label: 'DR ID',
    width: '7%',
    sortValue: r => r.opp_id,
    render: r => <span style={{
      fontSize: 9,
      color: '#8892a0'
    }} data-id="element-826" data-mp="2f3kx25qoexx9">{r.opp_id}</span>
  }, {
    key: 'acc_name',
    label: 'Account',
    width: '16%',
    sortValue: r => r.acc_name,
    render: r => <span title={r.acc_name} data-id="element-827" data-mp="kptozanzwm3x">{r.acc_name.substring(0, 24)}</span>
  }, {
    key: 'mergedSc',
    label: 'SC Assigned',
    width: '12%',
    sortValue: r => r.mergedSc,
    render: r => <span title={r.mergedSc} data-id="element-828" data-mp="31b8d6kzpz585">{r.mergedSc.split(',').slice(0, 2).join(', ')}</span>
  }, {
    key: 'displayStatus',
    label: 'Status',
    width: '8%',
    sortValue: r => r.displayStatus,
    render: r => statusBadge(r.displayStatus)
  }, {
    key: 'req_offering',
    label: 'Offering',
    width: '11%',
    sortValue: r => r.req_offering,
    render: r => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-829" data-mp="2dle145n85koy">
        {(r.req_offering || '').replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
      </span>
  }, {
    key: 'req_age',
    label: 'Age',
    width: '10%',
    sortValue: r => Math.max(...r.allRequests.map(req => req.req_age ?? 0)),
    render: r => <span style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px'
    }} data-id="element-830" data-mp="28nca8yjk4h2x">
        {r.allRequests.map((req, idx) => {
        const severity = ageSeverity(req.req_age);
        return <a key={req.req_id} href={getRequestURL(req)} target="_blank" rel="noopener noreferrer" className={severity === 'high' ? 'c-red' : severity === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: severity === 'high' ? 600 : 400,
          textDecoration: 'none',
          color: severity === 'high' ? '#DC2626' : severity === 'medium' ? '#D97706' : '#344054'
        }} title={`${req.req_name} - ${formatReqAge(req.req_age)}`} data-id="element-831" data-mp="6u4vbq7uw1hk" data-mpi={idx}>
              {formatReqAge(req.req_age)}{idx < r.allRequests.length - 1 ? ',' : ''}
            </a>;
      })}
      </span>
  }, {
    key: 'opp_gnarr_grand_total',
    label: 'GNARR',
    width: '9%',
    sortValue: r => r.opp_gnarr_grand_total,
    render: r => <>{fmtG(r.opp_gnarr_grand_total)}</>
  }, {
    key: 'opp_close_q',
    label: 'Close Q',
    width: '8%',
    sortValue: r => r.opp_close_q,
    render: r => <>{r.opp_close_q}</>
  }, {
    key: 'opp_region',
    label: 'Region',
    width: '7%',
    sortValue: r => r.opp_region,
    render: r => regionBadge(r.opp_region)
  }, {
    key: 'req_sub_region',
    label: 'Sub',
    width: '5%',
    sortValue: r => r.req_sub_region,
    render: r => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-832" data-mp="3cuhipv4v967j">{r.req_sub_region}</span>
  }];
  const agedColumns: SortableColumn<Request>[] = [{
    key: 'acc_name',
    label: 'Account',
    width: '30%',
    sortValue: r => r.acc_name,
    render: r => <span title={r.acc_name} data-id="element-833" data-mp="2p6zw0e0gkybf">{r.acc_name.substring(0, 22)}</span>
  }, {
    key: 'user_sc_name',
    label: 'SC',
    width: '20%',
    sortValue: r => r.user_sc_name,
    render: r => <span title={r.user_sc_name} data-id="element-834" data-mp="2u62q53ewhk4m">{r.user_sc_name.split(' ')[0]}</span>
  }, {
    key: 'req_age',
    label: 'Age',
    width: '12%',
    sortValue: r => r.req_age ?? 0,
    render: r => <span className="c-red" style={{
      fontWeight: 600
    }} data-id="element-835" data-mp="32z6ck17ix7be">{formatReqAge(r.req_age)}</span>
  }, {
    key: 'opp_gnarr_grand_total',
    label: 'GNARR',
    width: '18%',
    sortValue: r => r.opp_gnarr_grand_total,
    render: r => <>{fmtG(r.opp_gnarr_grand_total)}</>
  }, {
    key: 'opp_close_q',
    label: 'Close Q',
    width: '12%',
    sortValue: r => r.opp_close_q,
    render: r => <>{r.opp_close_q}</>
  }, {
    key: 'req_sub_region',
    label: 'Sub',
    width: '8%',
    sortValue: r => r.req_sub_region,
    render: r => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-836" data-mp="3niasw0epjdws">{r.req_sub_region}</span>
  }];
  return <div data-id="element-837" data-mp="3iixz0vpf56kp">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-838" data-mp="3iksj67sv8unq">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-839" data-mp="2ut3pan0eby4o" /> 
        <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-840" data-mp="2pw3q8w1yrvn2" />
        <MetricCard label="Aged >30 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-841" data-mp="33vmmorlexijj" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-842" data-mp="2zs6uc7gdm4cn" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-843" data-mp="f30vv6pocyvv" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-844" data-mp="ml8jo1jjgsoy">
        <div className="card" style={{
        minHeight: 260
      }} data-id="element-845" data-mp="mkyrq8exobgm">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4
        }} data-id="element-846" data-mp="rlmu1q8m4eq5">
            <h3 style={{
            margin: 0
          }} data-id="element-847" data-mp="cp5gcscyao0f">Requests by solution</h3>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 9,
            color: '#667085'
          }} data-id="element-848" data-mp="hmx9fppc0g67">
              <span data-id="element-849" data-mp="15c836n89laau">Top</span>
              <input type="range" min={1} max={offeringTotal} value={offeringTop} onChange={e => setOfferingTop(Number(e.target.value))} style={{
              width: 80,
              accentColor: '#3B82F6'
            }} data-id="element-850" data-mp="15e2pa4v9v8gi" />
              <span style={{
              minWidth: 20,
              textAlign: 'right'
            }} data-id="element-851" data-mp="10m1gisxbbzv8">{offeringTop}</span>
            </div>
          </div>
          <OfferingChart requests={all} top={offeringTop} onTopChange={setOfferingTop} onTotalChange={setOfferingTotal} selected={selectedSolution} onSelect={handleSolutionClick} onOthersChange={setOthersLabels} data-id="element-852" data-mp="1obvp2yaln29o" />
        </div>
        <div className="card" style={{
        minHeight: 260,
        maxHeight: 280,
        overflow: 'auto'
      }} data-id="element-853" data-mp="3kdpfumzk8qwt">
          <h3 data-id="element-854" data-mp="3gcdoabx4lvlz">Aged requests (&gt;30 days) at risk</h3>
          {aged.length > 0 ? <SortableTable columns={agedColumns} data={aged} rowKey={r => `${r.opp_id}`} defaultSort={{
          key: 'req_age',
          dir: 'desc'
        }} data-id="element-855" data-mp="3belv7ekqw3g7" /> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-856" data-mp="7yetvepsyial">No aged requests in this filter ✓</div>}
        </div>
      </div>

      {/* Opportunities — full list */}
      <SectionHeader title={<>
          Opportunities — full list
          {selectedSolution && <span onClick={() => setSelectedSolution(null)} style={{
        marginLeft: 8,
        fontSize: 9,
        fontWeight: 600,
        color: '#3B82F6',
        cursor: 'pointer',
        textTransform: 'none',
        letterSpacing: 'normal'
      }} data-id="element-858" data-mp="2npsxbckkbliy">
              · {selectedSolution} ✕
            </span>}
        </>} data-id="element-857" data-mp="3u3s8gm2rs2um" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }} data-id="element-859" data-mp="2iivtrdk8074g">
        <input type="text" placeholder="Search DR ID…" value={searchId} onChange={e => setSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 120,
        color: '#344054',
        background: '#fff'
      }} data-id="element-860" data-mp="2nbfhh3cmgimz" />
        <input type="text" placeholder="Search Account…" value={searchAccount} onChange={e => setSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-861" data-mp="368yipksu8wyu" />
        <input type="text" placeholder="Search SC…" value={searchSc} onChange={e => setSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-862" data-mp="3fxfxf9230xc1" />
        <div ref={statusDropdownRef} style={{
        position: 'relative',
        zIndex: statusDropdownOpen ? 10 : undefined
      }} data-id="element-863" data-mp="2julhq7wdhm1">
          <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-864" data-mp="7lk0fgy8qffe">
            {selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : 'Status ▾'}
          </button>
          {statusDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-865" data-mp="261j1q9mf4yig">
              <div onClick={() => setSelectedStatuses(selectedStatuses.length === allStatuses.length ? [] : [...allStatuses])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-866" data-mp="2tqlilf98anbw">
                {selectedStatuses.length === allStatuses.length ? 'Deselect all' : 'Select all'}
              </div>
              {allStatuses.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-867" data-mp="18f27xecw931g">
                  <input type="checkbox" checked={selectedStatuses.includes(s)} onChange={() => {
              setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
            }} data-id="element-868" data-mp="1arw0apvbv0yt" />
                  {s}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-869" data-mp="rumfb4actffg">
          <span style={{
          minWidth: 50
        }} data-id="element-870" data-mp="d2ask5ijcvdy">Age</span>
          <RangeSlider min={dataAgeMin} max={dataAgeMax} low={ageMin} high={ageMax} onChangeLow={setAgeMin} onChangeHigh={setAgeMax} width={100} data-id="element-871" data-mp="i2p3bvbiujfo" />
          <span style={{
          fontSize: 9,
          color: '#8892a0',
          minWidth: 70
        }} data-id="element-872" data-mp="4637sqz4p6of">{ageMin}–{ageMax}d</span>
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-873" data-mp="3lmehrsfjgg7o">
          <span style={{
          minWidth: 50
        }} data-id="element-874" data-mp="3gnl15r4yg93a">GNARR</span>
          <RangeSlider min={dataGnarrMin} max={dataGnarrMax} low={gnarrMin} high={gnarrMax} onChangeLow={setGnarrMin} onChangeHigh={setGnarrMax} width={100} data-id="element-875" data-mp="3gow6vwhynqx1" />
          <span style={{
          fontSize: 9,
          color: '#8892a0',
          minWidth: 70
        }} data-id="element-876" data-mp="3pnq6zwui2zfc">${(gnarrMin / 1e6).toFixed(0)}M–${(gnarrMax / 1e6).toFixed(0)}M</span>
        </div>
        <div ref={closeQDropdownRef} style={{
        position: 'relative',
        zIndex: closeQDropdownOpen ? 10 : undefined
      }} data-id="element-877" data-mp="8djgwq28eczn">
          <button onClick={() => setCloseQDropdownOpen(!closeQDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-878" data-mp="h7y73noav7xj">
            {selectedCloseQs.length > 0 ? `Close Q (${selectedCloseQs.length})` : 'Close Q ▾'}
          </button>
          {closeQDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-879" data-mp="1a9xlgqqm2p9i">
              <div onClick={() => setSelectedCloseQs(selectedCloseQs.length === allCloseQs.length ? [] : [...allCloseQs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-880" data-mp="nbb2ticp86ub">
                {selectedCloseQs.length === allCloseQs.length ? 'Deselect all' : 'Select all'}
              </div>
              {allCloseQs.map(q => <label key={q} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-881" data-mp="27sh71vf3jdle">
                  <input type="checkbox" checked={selectedCloseQs.includes(q)} onChange={() => {
              setSelectedCloseQs(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);
            }} data-id="element-882" data-mp="3s85jfqliys5x" />
                  {q}
                </label>)}
            </div>}
        </div>
        <div ref={regionDropdownRef} style={{
        position: 'relative',
        zIndex: regionDropdownOpen ? 10 : undefined
      }} data-id="element-883" data-mp="39ka96gp4fwhk">
          <button onClick={() => setRegionDropdownOpen(!regionDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-884" data-mp="2zjzv6tv9il1c">
            {selectedRegions.length > 0 ? `Region (${selectedRegions.length})` : 'Region ▾'}
          </button>
          {regionDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-885" data-mp="2ibgvnqbh3dqt">
              <div onClick={() => setSelectedRegions(selectedRegions.length === allRegions.length ? [] : [...allRegions])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-886" data-mp="3fyfrxzy96jo6">
                {selectedRegions.length === allRegions.length ? 'Deselect all' : 'Select all'}
              </div>
              {allRegions.map(r => <label key={r} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-887" data-mp="1uiqjph3kkugj">
                  <input type="checkbox" checked={selectedRegions.includes(r)} onChange={() => {
              setSelectedRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
            }} data-id="element-888" data-mp="1wzrr1igb4lbe" />
                  {r}
                </label>)}
            </div>}
        </div>
        <div ref={subDropdownRef} style={{
        position: 'relative',
        zIndex: subDropdownOpen ? 10 : undefined
      }} data-id="element-889" data-mp="1e2gsy16evxxj">
          <button onClick={() => setSubDropdownOpen(!subDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-890" data-mp="lkcsia8g9le6">
            {effectiveSubs.length > 0 ? `Sub (${effectiveSubs.length})` : 'Sub ▾'}
          </button>
          {subDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-891" data-mp="91g4nwt69j1c">
              <div onClick={() => setSelectedSubs(effectiveSubs.length === availableSubs.length ? [] : [...availableSubs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-892" data-mp="nquipi7p0on2">
                {effectiveSubs.length === availableSubs.length ? 'Deselect all' : 'Select all'}
              </div>
              {availableSubs.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-893" data-mp="2m9zcm03pkrql">
                  <input type="checkbox" checked={effectiveSubs.includes(s)} onChange={() => handleSubToggle(s)} data-id="element-894" data-mp="288wolht5l7d1" />
                  {s}
                </label>)}
            </div>}
        </div>
      </div>
      <div className="tw" data-id="element-895" data-mp="2w62c0y76ec9u">
        {searchRows ? <table className="tbl" data-id="element-896" data-mp="2w4rbd7911kmy">
            <thead data-id="element-897" data-mp="2r7r1smcyxzr3">
              <tr data-id="element-898" data-mp="2r8ak446j6q5n">
                {mergedColumns.map(col => <th key={col.key} style={{
              width: col.width
            }} data-id="element-899" data-mp="3omwjufh208ty">{col.label}</th>)}
              </tr>
            </thead>
            <tbody data-id="element-900" data-mp="3jvwr7meywcx9">{searchRows}</tbody>
          </table> : <SortableTable columns={mergedColumns} data={mergedFiltered} rowKey={r => r.opp_id} data-id="element-901" data-mp="3bpyjt2eglhwg" />}
      </div>

    </div>;
}